import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import classNames from 'classnames';
import slugify from 'slugify';

import { Svg } from '@components/Svg';

import { NAVIGATION_HEIGHT } from '@constants/dimensions';
import { recipeMessages } from '@data/ui-messages/recipes';
import { useOnClickAway } from '@hooks/useOnClickAway';
import { RecipeFilterCriteriaProps } from '@interfaces/common';

import styles from './RecipeFilterCriteria.module.css';

interface RecipeFilterCriteria extends RecipeFilterCriteriaProps {
	onChange: (checked: Record<string, any[]>) => void;
}

export const RecipeFilterCriteria: React.VFC<RecipeFilterCriteria> = ({
	title,
	name,
	options,
	onChange,
}) => {
	const intl = useIntl();
	const [checked, setChecked] = useState<
		Record<string, string | number | null>
	>({});
	const [isExpanded, setIsExpanded] = useState(false);
	const elementRef = useRef<HTMLDivElement>(null);
	const hasClickedAway = useOnClickAway(elementRef);
	const popupId = `recipe-${slugify(title).toLowerCase()}-filter`;
	const optionsEl = useRef<HTMLUListElement>(null);

	const handleOnChange = (event: React.FormEvent<HTMLInputElement>) => {
		const target = event.currentTarget;
		const value = Number.isNaN(Number(target.value)) // NOTE: Usage as suggested by Airbnb: https://github.com/airbnb/javascript#standard-library--isnan
			? target.value
			: parseInt(target.value, 10);

		const newValue = { ...checked };

		if (target.checked) {
			newValue[target.id] = value;
		} else {
			delete newValue[target.id];
		}

		setChecked(newValue);

		onChange({
			[name]: Object.values(newValue).filter((item) => item),
		});
	};

	useEffect(() => {
		if (hasClickedAway) {
			setIsExpanded(false);
		}
	}, [hasClickedAway]);

	// NOTE: Ensures that the options always appear within view
	useEffect(() => {
		if (optionsEl?.current) {
			const { top, left, right } = optionsEl.current.getBoundingClientRect();

			if (
				Math.ceil(right) >= window.innerWidth &&
				left > window.innerWidth / 2
			) {
				optionsEl.current.style.right = '0';
				optionsEl.current.style.left = 'initial';
			} else {
				optionsEl.current.style.right = 'initial';
				optionsEl.current.style.left = '0';
			}

			if (top - NAVIGATION_HEIGHT > window.innerHeight / 2) {
				optionsEl.current.style.bottom = 'var(--button-height)';
				optionsEl.current.style.top = 'initial';
			} else {
				optionsEl.current.style.bottom = 'initial';
				optionsEl.current.style.top = 'var(--button-height)';
			}
		}
	}, [isExpanded]);

	return (
		<div
			className={styles.wrapper}
			data-testid="RecipeFilterCriteria"
			ref={elementRef}
		>
			<button
				aria-controls={popupId}
				aria-expanded={isExpanded}
				aria-haspopup="true"
				aria-label={
					isExpanded
						? intl.formatMessage(recipeMessages.closeRecipeFilterLabel, {
								filter: title.toLowerCase(),
						  })
						: intl.formatMessage(recipeMessages.openRecipeFilterLabel, {
								filter: title.toLowerCase(),
						  })
				}
				className={classNames(styles.button, 'fluid-font-t6-bold', {
					[styles['arrow-up']]: isExpanded,
				})}
				onClick={() => setIsExpanded(!isExpanded)}
				type="button"
			>
				{title}
				<span className={styles.icon}>
					<Svg id="arrow" />
				</span>
			</button>

			<ul
				aria-hidden={!isExpanded}
				aria-label={intl.formatMessage(recipeMessages.filterCriteriaListLabel)}
				className={classNames(styles.options, 'stack-block', {
					[styles['is-open']]: isExpanded,
				})}
				id={popupId}
				ref={optionsEl}
			>
				{options.map(({ title: optionTitle, name: fieldName, value }) => (
					<li className={classNames(styles.option)} key={fieldName}>
						<input
							checked={Boolean(checked[fieldName] != null)}
							className={classNames('checkbox', styles.input)}
							id={fieldName}
							name={name}
							onChange={handleOnChange}
							type="checkbox"
							value={value}
						/>

						<label
							className={classNames(styles.label, 'fluid-font-t6')}
							htmlFor={fieldName}
						>
							{optionTitle}
						</label>
					</li>
				))}
			</ul>
		</div>
	);
};
